import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  Download, Loader2, ArrowLeft, Share2, RefreshCw, AlertTriangle,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import ReportContent from "@/components/ReportContent";

const Report = ({ adminMode = false }) => {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [retryBusy, setRetryBusy] = useState(false);
  const ref = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const autoDownload = searchParams.get("download") === "1";
  const backPath = adminMode ? "/admin" : "/dashboard";

  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);

  useEffect(() => {
    const load = async () => {
      try { const res = await api.get(adminMode ? `/admin/assessments/${id}` : `/assessments/${id}`); setData(res.data); }
      catch (e) { toast.error("Report not found"); navigate(backPath); }
      finally { setBusy(false); }
    };
    load();
  }, [id, adminMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async () => {
    if (!ref.current) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#F8FAFC", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      const pageH = pdf.internal.pageSize.getHeight();
      let pos = 0, remaining = h;
      pdf.addImage(img, "PNG", 0, pos, w, h);
      remaining -= pageH;
      while (remaining > 0) {
        pos -= pageH; pdf.addPage();
        pdf.addImage(img, "PNG", 0, pos, w, h);
        remaining -= pageH;
      }
      pdf.save(`CareerCompass-Report${data?.candidate_name ? `-${data.candidate_name.replace(/[^\w-]+/g, "_")}` : ""}.pdf`);
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    if (!autoDownload || !data || busy) return;
    const t = setTimeout(async () => {
      toast.info("Preparing PDF download…");
      await downloadPdf();
      setSearchParams({}, { replace: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [autoDownload, data, busy]); // eslint-disable-line react-hooks/exhaustive-deps

  const share = async () => {
    const url = `${window.location.origin}/shared/${data.share_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard!");
    } catch (e) {
      window.prompt("Copy your share link:", url);
    }
  };

  const retry = async () => {
    setRetryBusy(true);
    try {
      const res = await api.post(`/assessments/${id}/regenerate`);
      setData(res.data);
      if (res.data.report?.generated === false) toast.error("Still couldn't generate. Try again shortly.");
      else toast.success("Report regenerated!");
    } catch (e) { toast.error("Regeneration failed. Try again shortly."); }
    finally { setRetryBusy(false); }
  };

  if (busy || !data) {
    return (<div className="min-h-screen bg-[#F8FAFC]"><Navbar />
      <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></div>);
  }

  const failed = data.report?.generated === false;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backPath)} data-testid="back-to-dashboard"
                  className="font-heading font-bold text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1" /> {adminMode ? "Access codes" : "Dashboard"}
          </Button>
          <div className="flex items-center gap-2">
            {adminMode && data.candidate_name && (
              <span className="font-body text-sm text-slate-500 mr-2" data-testid="report-candidate-label">
                Candidate: <span className="font-semibold text-slate-800">{data.candidate_name}</span> · <span className="font-mono">{data.code}</span>
              </span>
            )}
            <Button variant="outline" onClick={share} data-testid="share-report-btn"
                    className="rounded-xl font-heading font-bold">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
            <Button onClick={downloadPdf} disabled={pdfBusy} data-testid="download-pdf-btn"
                    className="rounded-xl font-heading font-bold transition-transform hover:scale-[1.02]">
              {pdfBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Download PDF
            </Button>
          </div>
        </div>

        {failed && !adminMode && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="report-retry-banner">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-heading font-bold text-amber-900">The written report didn't generate this time.</p>
              <p className="font-body text-sm text-amber-800/80 mt-1">Your scores and charts below are accurate. Tap retry to regenerate the personalized narrative.</p>
            </div>
            <Button onClick={retry} disabled={retryBusy} data-testid="retry-report-btn"
                    className="rounded-xl font-heading font-bold bg-amber-500 hover:bg-amber-600">
              {retryBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Retry
            </Button>
          </div>
        )}

        <div ref={ref}>
          <ReportContent data={data} />
        </div>
      </div>
    </div>
  );
};

export default Report;
